import { useEffect, useState, useCallback } from "react";
import api, { fmtRM, fmtDate, downloadFile } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Receipt, Download, FileSpreadsheet } from "lucide-react";

const METHODS = { bank_transfer: "Bank Transfer", online: "Online", counter: "Counter", financing: "Financing" };
const TYPES = { deposit: "Deposit", installment: "Installment", progress: "Progress", full: "Full Payment" };

export default function Payments() {
  const { t } = useLang();
  const [payments, setPayments] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ booking_id: "", amount: "", method: "bank_transfer", payment_type: "installment" });

  const load = useCallback(() => {
    api.get("/payments").then((r) => setPayments(r.data)).catch(() => toast.error("Failed"));
    api.get("/bookings").then((r) => setBookings(r.data.filter((b) => b.outstanding > 0))).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const recordPayment = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post("/payments", { ...form, amount: Number(form.amount) });
      toast.success(`Receipt ${res.data.receipt_no} generated — ${fmtRM(res.data.amount)}`);
      setOpen(false);
      setForm({ booking_id: "", amount: "", method: "bank_transfer", payment_type: "installment" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  if (!payments) return <div className="flex justify-center h-64 items-center" data-testid="payments-loading"><div className="w-8 h-8 border-4 border-brand-800 border-t-gold-500 rounded-full animate-spin" /></div>;

  const totalCollected = payments.reduce((s, p) => s + p.amount, 0);

  return (
    <div className="space-y-5" data-testid="payments-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-sans text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">{t("app.payments")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{payments.length} receipts • {fmtRM(totalCollected)} collected all-time</p>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="export-payments-csv" variant="outline" onClick={() => downloadFile("/reports/payments.csv", "payments.csv")}
            className="rounded-full border-sand-border hover:border-emerald-700/40 hover:text-emerald-800">
            <FileSpreadsheet className="w-4 h-4 mr-1.5" />Export
          </Button>
          <Button data-testid="record-payment-button" onClick={() => setOpen(true)} className="bg-brand-800 hover:bg-brand-700 text-sand rounded-full">
            <Plus className="w-4 h-4 mr-1.5" />Record Payment
          </Button>
        </div>
      </div>

      <div className="pro-card overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead>
            <tr className="border-b border-sand-border text-left bg-[#F8F6F2]/80">
              {["Receipt", "Booking", "Customer", "Type", "Method", "Amount", t("common.date"), "PDF"].map((h) => (
                <th key={h} className="px-4 py-3 text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {payments.map((p) => (
              <tr key={p.payment_id} data-testid={`payment-row-${p.receipt_no}`} className="border-b border-sand-border/60 hover:bg-emerald-50/60 transition-colors">
                <td className="px-4 py-3">
                  <span className="inline-flex items-center gap-1.5 font-mono text-xs text-brand-800">
                    <Receipt className="w-3.5 h-3.5 text-gold-600" />{p.receipt_no}
                  </span>
                </td>
                <td className="px-4 py-3 font-mono text-xs">{p.booking_no}</td>
                <td className="px-4 py-3 font-medium text-brand-950">{p.customer_name}</td>
                <td className="px-4 py-3 text-xs">{TYPES[p.payment_type] || p.payment_type}</td>
                <td className="px-4 py-3 text-xs">{METHODS[p.method] || p.method}</td>
                <td className="px-4 py-3 font-mono text-xs text-emerald-700 font-semibold">{fmtRM(p.amount)}</td>
                <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">{fmtDate(p.created_at)}</td>
                <td className="px-4 py-3">
                  <button data-testid={`download-receipt-${p.receipt_no}`}
                    onClick={() => downloadFile(`/payments/${p.payment_id}/receipt.pdf`, `${p.receipt_no}.pdf`)}
                    className="p-1.5 rounded-lg text-slate-400 hover:text-emerald-800 hover:bg-emerald-50 transition-colors">
                    <Download className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {payments.length === 0 && <div className="text-center py-12 text-muted-foreground text-sm">No payments yet</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="record-payment-dialog" className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-sans font-semibold text-lg">Record Payment</DialogTitle></DialogHeader>
          <form onSubmit={recordPayment} className="space-y-4">
            <div>
              <Label>Booking (outstanding)</Label>
              <Select value={form.booking_id} onValueChange={(v) => setForm({ ...form, booking_id: v })} required>
                <SelectTrigger data-testid="payment-booking-select"><SelectValue placeholder="Select booking" /></SelectTrigger>
                <SelectContent>
                  {bookings.map((b) => (
                    <SelectItem key={b.booking_id} value={b.booking_id}>
                      {b.booking_no} — {b.customer_name} ({fmtRM(b.outstanding)})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Amount (RM)</Label>
              <Input data-testid="payment-amount-input" type="number" min="1" step="0.01" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Method</Label>
                <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                  <SelectTrigger data-testid="payment-method-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(METHODS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Type</Label>
                <Select value={form.payment_type} onValueChange={(v) => setForm({ ...form, payment_type: v })}>
                  <SelectTrigger data-testid="payment-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(TYPES).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button data-testid="payment-submit-button" type="submit" className="w-full bg-brand-800 hover:bg-brand-700 text-sand rounded-full">
              Generate Receipt
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
